// AgentNavBar — Phase 2 fork of TopPillNav for the agent role.
//
// Visual identity:
//   - Active pill = brand magenta (not the admin app's violet).
//   - Inactive labels = arena-tinted muted (light) / neon-muted (dark).
//   - Glass strip with arena-tinted border, magenta-tinted glow shadow.
//
// Structure mirrors TopPillNav: brand mark on the left, centre pills
// on lg+, utility cluster (bell + theme + avatar) on the right. The
// centre pills hide on mobile (AgentBottomNav takes over).
//
// Admin role short-circuits to null — admin keeps TopPillNav.

import { NavLink, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../lib/adminAuth';
import { useT } from '../../lib/i18n';
import AdminThemeToggle from './AdminThemeToggle';
import AdminLanguageSwitcher from './AdminLanguageSwitcher';
import AgentWalletChip from './AgentWalletChip';
import BaaliLogo from './BaaliLogo';
import { SseAlertsBell } from '../lib/sseAlerts';

// 3-level hierarchy display names (mirrors shared/src/core/lib/levelNames.js).
const LEVEL_NAMES: Record<number, string> = { 1: 'Super Master', 2: 'Master', 3: 'Agent' };

type Item = {
  to: string;
  /** i18n key — resolved at render via useT(). Replaces the
   *  Phase B Group 1 hardcoded `label: string` (2026-05-12). */
  labelKey: string;
  matchPrefix?: string;
  permissionKey?: string;
  /** Hide this tab if the agent's own level is GREATER than this. Keeps leaf
   *  Agents (L3) from seeing the sub-agent-creation tab at all. */
  maxLevel?: number;
};

// Phase 3.5: "Money" retired — its surface area was absorbed into the
// Players hub (and later the Agents hub).
// Phase 8 (2026-05-11): "More" retired — its only surviving useful
// destination was Rewards, so the desktop pill nav goes directly to
// /rewards instead of forcing an extra click through /more. Same
// 4-pill geometry; only the 4th pill's destination + label moved.
// Phase 16 (2026-05-12): Profile pill added. The bottom nav has had a
// Profile tab since Phase 3.5 but lg+ users (where AgentBottomNav is
// hidden) had no in-app way to reach /profile from the top bar — only
// the avatar in the right utility cluster, which was later removed in
// Phase 4. The pill resolves that gap with a localised label so the
// flow matches the bottom-nav surface on smaller screens.
// U2 (2026-07-09) — 6 pills → 5, mirroring AgentBottomNav:
//   * Cricket's pill becomes GAMES (/games) — all games' earnings, with
//     cricket's detail at /games/cricket.
//   * Rewards pill removed — the offers surface folds into Profile (U4);
//     old /rewards URLs resolve via router redirects.
const ITEMS: Item[] = [
  { to: '/',                labelKey: 'agent.nav.dashboard' },
  { to: '/games',           labelKey: 'agent.nav.games',    matchPrefix: '/games' },
  { to: '/people/players',  labelKey: 'agent.nav.players',  matchPrefix: '/people/players',  permissionKey: 'users' },
  { to: '/people/agents',   labelKey: 'agent.nav.agents',   matchPrefix: '/people/agents',   permissionKey: 'create_subagent', maxLevel: 2 },
  { to: '/profile/wallet',  labelKey: 'agent.nav.money',    matchPrefix: '/profile' },
];

export default function AgentNavBar() {
  const { admin, canDo } = useAdminAuth();
  const t = useT();
  const loc = useLocation();
  if (!admin || admin.role !== 'agent') return null;
  const items = ITEMS.filter((i) =>
    (!i.permissionKey || canDo(i.permissionKey, 'view')) &&
    (i.maxLevel == null || admin.level == null || admin.level <= i.maxLevel));
  // The downline-management tab is named for what THIS agent creates: a Super Master
  // creates Masters, a Master creates Agents (an Agent has the tab hidden entirely).
  const childLabelPlural = `${LEVEL_NAMES[Number(admin.level ?? 1) + 1] || 'Agent'}s`;
  const labelFor = (i: Item) => (i.to === '/people/agents' ? childLabelPlural : t(i.labelKey));
  const isActive = (i: Item) =>
    i.matchPrefix
      ? loc.pathname === i.to || loc.pathname.startsWith(i.matchPrefix + '/')
      : loc.pathname === i.to;

  return (
    <header
      // Phase 10 follow-up (2026-05-12): top bar now stacks ABOVE
      // the warning + lockout modals (z-9000 and z-9999 respectively)
      // so the agent can still tap the wallet chip, alert bell, and
      // theme toggle without dismissing the crisis modal. The modals
      // pad their top so the bar visually sits above them with a
      // safe-area-aware gutter; the bar's semi-transparent background
      // + backdrop blur lets the modal show through softly.
      className="sticky top-0 z-[10000] px-3 lg:px-6 pt-3 lg:pt-5"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <div
        className="mx-auto max-w-screen-2xl flex items-center gap-3
                   rounded-2xl px-3 lg:px-4 h-14 lg:h-16
                   bg-white/85 dark:bg-arena-800/70 backdrop-blur-xl
                   border border-arena-200/60 dark:border-arena-600/60
                   shadow-[0_8px_24px_-12px_rgba(var(--brand-rgb),0.25)]
                   dark:shadow-[0_8px_32px_-12px_rgba(var(--brand-rgb),0.4)]"
      >
        {/* Brand mark — magenta wordmark via BaaliLogo for agent role */}
        <NavLink to="/" className="shrink-0">
          {/* Top bar shows the ROLE badge only — no username (owner preference). */}
          <BaaliLogo role={admin.role} levelLabel={LEVEL_NAMES[Number(admin.level ?? 1)] || 'Agent'} />
        </NavLink>

        {/* Centre pill nav — desktop only */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-1">
          {items.map((i) => {
            const active = isActive(i);
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === '/'}
                className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition
                            ${active
                              ? 'bg-brand-500 text-white dark:bg-brand-500 dark:text-white shadow-[0_2px_12px_rgba(var(--brand-rgb),0.5)]'
                              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-neon-100'}`}
              >
                {labelFor(i)}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex-1 lg:hidden" />

        {/* Right utility cluster — wallet chip (Phase 3), bell, theme
            toggle. Phase 4 follow-up (2026-05-11): the avatar moved
            OUT of this cluster and INTO the bottom-nav Profile tab —
            both to save horizontal space on phones and to mirror the
            player-end pattern where the avatar lives on the Profile
            bottom-nav button. Desktop users can still tap into
            /profile via the Profile link in the centre pill nav (no
            "Profile" pill yet — TODO: add the pill or rely on the
            bottom nav being visible on lg+ too). */}
        {/* Right utility cluster. Phase 10 follow-up (2026-05-12):
            AdminLanguageSwitcher slots in BETWEEN the bell and the
            theme toggle — exactly the spot the player NavBar uses
            for its LanguageSwitcher (between auth chip and theme).
            Same component family, same z-layer behaviour, identical
            premium dropdown styling — so the agent app looks like
            a colour-flipped sibling of the player app rather than
            a different product. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <AgentWalletChip />
          <SseAlertsBell endpoint="/api/admin/me-agent/sse/alerts" />
          <AdminLanguageSwitcher />
          <AdminThemeToggle compact />
        </div>
      </div>
    </header>
  );
}
